<template>
  <BModal
    id="modal-assign-credential"
    centered
    hide-footer
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    size="md"
    header-class="border-0 pb-1 pt-2 bg-white"
    body-class="px-2 pb-2 pt-0 bg-white"
  >
    <template #modal-title>
      <span class="h3 text-dark font-weight-bold mb-5">Bagikan Data Credential</span>
    </template>

    <!-- Konteks credential yang sedang di-assign -->
    <p
      class="text-dark mb-2"
      style="font-size: 14px;"
    >
      {{ selectedItem ? `${selectedItem.platform} (${selectedItem.email})` : '' }}
    </p>

    <!-- Search Role -->
    <BFormGroup
      label="Cari Role"
      class="mb-1"
    >
      <div class="position-relative">
        <BFormInput
          v-model="searchKeyword"
          placeholder="Cari dan bagikan dengan Role Akses tertentu"
          @input="handleSearch()"
        />

        <!-- Dropdown -->
        <div
          v-if="searchKeyword && filteredRoles.length > 0"
          class="border rounded bg-white w-100 no-scrollbar"
          style="max-height: 180px; overflow-y: auto; position: absolute; z-index: 9999; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"
        >
          <div
            v-for="role in filteredRoles"
            :key="role.id"
            class="px-1 py-1 cursor-pointer role-item"
            style="font-size: 13px;"
            @click="selectRole(role)"
          >
            {{ role.name }}
          </div>
        </div>
      </div>

      <!-- Chips di bawah input -->
      <div
        v-if="selectedRoles.length > 0"
        class="d-flex flex-wrap mt-2"
        style="gap: 6px;"
      >
        <div
          v-for="role in selectedRoles"
          :key="role.id"
          class="d-flex align-items-center rounded-full px-1 py-1"
          style="background: #F8F8F8; font-size: 12px; gap: 4px;"
        >
          <span style="color: #333;">{{ role.name }}</span>
          <span
            class="cursor-pointer"
            style="color: #333; font-size: 14px; font-weight: bold; line-height: 1; margin-left: 2px;"
            @click="removeRole(role)"
          >×</span>
        </div>
      </div>
    </BFormGroup>

    <!-- Role dengan Akses -->
    <div class="mt-2">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span style="font-size: 13px; font-weight: 600;">Role dengan Akses</span>
        <span style="font-size: 13px; font-weight: 600;">Hapus Akses</span>
      </div>

      <div
        v-if="assignedRoles.length === 0"
        class="text-muted"
        style="font-size: 13px;"
      >
        Belum ada role dengan akses.
      </div>

      <div class="d-flex flex-column gap-6">
        <div
          v-for="role in assignedRoles"
          :key="role.id"
          class="d-flex justify-content-between align-items-center"
        >
          <span style="font-size: 13px;">• {{ role.name }}</span>
          <div
            class="d-flex align-items-center justify-content-center rounded cursor-pointer"
            style="width: 20px; height: 20px; border: 2px solid #ccc;"
            :style="revokeIds.includes(role.id) ? 'border-color: #F95031; background: #F95031;' : ''"
            @click="toggleRevoke(role.id)"
          >
            <span
              v-if="revokeIds.includes(role.id)"
              style="color: white; font-size: 14px; font-weight: bold; line-height: 1;"
            >—</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Buttons -->
    <div
      class="d-flex mt-5"
      style="gap: 10px;"
    >
      <BButton
        variant="link"
        class="button-orange w-50"
        @click="handleCancel"
      >
        Batal
      </BButton>
      <BButton
        variant="link"
        class="w-50"
        :class="{
          'button-orange-active': isActionEnabled,
          'button-disabled': !isActionEnabled,
        }"
        :disabled="!isActionEnabled || loading"
        @click="handleShare"
      >
        <BSpinner
          v-if="loading"
          small
        />
        <span v-else>Bagikan</span>
      </BButton>
    </div>
  </BModal>
</template>

<script>
import _ from 'lodash'
import { credentialAxios } from './config'

export default {
  name: 'AssignCredential',

  emits: ['assigned', 'assign-error', 'pin-session-expired'],

  data() {
    return {
      loading: false,
      searchKeyword: '',
      selectedItem: null,
      selectedRoles: [],
      revokeIds: [],
      assignedRoles: [],
      allRoles: [],
    }
  },

  computed: {
    filteredRoles() {
      return this.allRoles.filter(role => !this.isSelected(role))
    },

    isActionEnabled() {
      return this.selectedRoles.length > 0 || this.revokeIds.length > 0
    },
  },

  methods: {
    isSelected(role) {
      return this.selectedRoles.some(r => r.id === role.id)
    },

    selectRole(role) {
      this.selectedRoles.push(role)
      this.searchKeyword = ''
      this.allRoles = []
    },

    removeRole(role) {
      this.selectedRoles = this.selectedRoles.filter(r => r.id !== role.id)
    },

    toggleRevoke(id) {
      this.revokeIds = this.revokeIds.includes(id)
        ? this.revokeIds.filter(i => i !== id)
        : [...this.revokeIds, id]
    },

    handleSearch: _.debounce(function search() {
      this.searchRoles(this.searchKeyword)
    }, 500),

    async searchRoles(keyword) {
      if (!keyword) {
        this.allRoles = []
        return
      }
      this.loadingSearch = true
      try {
        const res = await credentialAxios.get(
          `v1/credential_management/${this.selectedItem.id}/available_roles`,
          { params: { search: keyword } },
        )
        this.allRoles = res.data.data.map(r => ({
          id: r.id,
          name: r.name,
        }))
      } catch {
        this.allRoles = []
      } finally {
        this.loadingSearch = false
      }
    },

    buildPayload() {
      return {
        assign_access_role_ids: this.selectedRoles.map(r => r.id),
        revoke_access_role_ids: this.revokeIds,
      }
    },

    async handleShare() {
      if (!this.selectedItem) return
      this.loading = true
      const payload = this.buildPayload()
      try {
        await credentialAxios.post(
          `v1/credential_management/secure/${this.selectedItem.id}/assign_credential`,
          payload,
        )
        this.$emit('assigned', {
          hasRevoke: this.revokeIds.length > 0 && this.selectedRoles.length === 0,
        })
        this.hide()
      } catch (err) {
        if (err.response?.status === 403) {
          this.hideModal()
          this.$emit('pin-session-expired', {
            onValid: () => this.handleShare(),
          })
          return
        }

        this.$emit('assign-error', err)
      } finally {
        this.loading = false
      }
    },

    handleCancel() {
      this.hide()
    },

    hide() {
      this.reset()
      this.$bvModal.hide('modal-assign-credential')
    },

    hideModal() {
      this.$bvModal.hide('modal-assign-credential')
    },

    async show(item) {
      this.selectedItem = item
      this.selectedRoles = []
      this.revokeIds = []
      this.searchKeyword = ''
      this.loading = false
      this.allRoles = []

      try {
        const assignedRes = await credentialAxios.get(
          `v1/credential_management/${item.id}/assigned_roles`,
        )
        this.assignedRoles = assignedRes.data.data.map(r => ({
          id: r.id,
          name: r.name,
        }))
      } catch {
        this.assignedRoles = []
      }

      this.$bvModal.show('modal-assign-credential')
    },

    reset() {
      this.selectedItem = null
      this.selectedRoles = []
      this.revokeIds = []
      this.searchKeyword = ''
      this.assignedRoles = []
      this.allRoles = []
      this.loading = false
    },
  },
}
</script>

<style scoped>
.role-item:hover {
  background-color: #f7dfcd;
  color: #F95031;
  transition: all 0.2s ease;
}
</style>

<style lang="scss" scoped>
@import './CredentialManagement.scss';
</style>
